// src/Dashboard/pages/teacher/Belts/utils/beltDashboardUtils.js

export const DEFAULT_USER_PHOTO = "/img/user_avatar.jpg";
export const DEFAULT_BELT_PHOTO = "/img/belt/ceinture_inconnue.png";

export function getTodayInputDate() {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function dateToInputValue(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function formatDateFr(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR");
}

export function getPreviousSeason(currentSeason) {
  const [startYear, endYear] = String(currentSeason || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return "";

  return `${startYear - 1}-${endYear - 1}`;
}

export function getNextSeason(currentSeason) {
  const [startYear, endYear] = String(currentSeason || "")
    .split("-")
    .map(Number);

  if (!startYear || !endYear) return "";

  return `${startYear + 1}-${endYear + 1}`;
}

export function buildSeasonOptions(activeSeason) {
  if (!activeSeason) return [];

  return [getPreviousSeason(activeSeason), activeSeason, getNextSeason(activeSeason)].filter(
    Boolean
  );
}

export function normalizeString(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function toAbsoluteUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  return `${base}${value}`;
}

export function getAdherentName(adherent = {}) {
  return `${String(adherent?.nom || "").toUpperCase()} ${
    adherent?.prenom || ""
  }`.trim();
}

export function getAdherentPhoto(adherent = {}) {
  return (
    toAbsoluteUrl(
      adherent?.photo_url ||
        adherent?.photoURL ||
        adherent?.photo ||
        adherent?.avatar
    ) || DEFAULT_USER_PHOTO
  );
}

export function getAdherentGroupsLabel(adherent = {}) {
  const groups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];

  return groups
    .map((group) => group?.nom)
    .filter(Boolean)
    .join(", ");
}

export function getCurrentHistory(adherentId, histories = {}) {
  return histories?.[adherentId] || null;
}

export function getCurrentBeltName(adherentId, histories = {}) {
  return getCurrentHistory(adherentId, histories)?.BeltColor?.nom || "";
}

export function getCurrentBeltImage(adherentId, histories = {}) {
  const history = getCurrentHistory(adherentId, histories);

  if (!history?.BeltColor?.belt_url) return DEFAULT_BELT_PHOTO;

  return `/img/belt/${history.BeltColor.belt_url}`;
}

export function getCurrentBeltDate(adherentId, histories = {}) {
  return getCurrentHistory(adherentId, histories)?.date_passage || null;
}

export function sortBelts(belts = []) {
  return [...belts].sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
}

export function filterAdherentsForBelts({ adherents = [], histories = {}, filters = {} }) {
  const query = normalizeString(filters.query);

  return adherents.filter((adherent) => {
    const fullName = normalizeString(
      `${adherent?.nom || ""} ${adherent?.prenom || ""}`
    );

    const groupNames = Array.isArray(adherent?.Groups)
      ? adherent.Groups.map((group) => group?.nom || "")
      : [];

    const currentBeltName = getCurrentBeltName(adherent.id, histories);
    const hasHistory = !!getCurrentHistory(adherent.id, histories);

    const matchesQuery = !query || fullName.includes(query);

    const matchesSeason =
      !filters.season || adherent?.saison_active === filters.season;

    const matchesGroup =
      !filters.group || groupNames.some((name) => name === filters.group);

    const matchesBelt = !filters.belt || currentBeltName === filters.belt;

    const matchesHistoryStatus =
      filters.historyStatus === "All" ||
      (filters.historyStatus === "with_history" && hasHistory) ||
      (filters.historyStatus === "without_history" && !hasHistory);

    return (
      matchesQuery &&
      matchesSeason &&
      matchesGroup &&
      matchesBelt &&
      matchesHistoryStatus
    );
  });
}

export function sortAdherentsForBelts(adherents = [], sort = "name_asc") {
  return [...adherents].sort((a, b) => {
    const nameA = normalizeString(`${a?.nom || ""} ${a?.prenom || ""}`);
    const nameB = normalizeString(`${b?.nom || ""} ${b?.prenom || ""}`);

    const dateA = a?.date_naissance ? new Date(a.date_naissance).getTime() : 0;
    const dateB = b?.date_naissance ? new Date(b.date_naissance).getTime() : 0;

    if (sort === "name_desc") return nameB.localeCompare(nameA, "fr");
    if (sort === "birth_asc") return dateA - dateB;
    if (sort === "birth_desc") return dateB - dateA;

    return nameA.localeCompare(nameB, "fr");
  });
}

export function calculateBeltStats({ allAdherents = [], filteredAdherents = [], histories = {} }) {
  const withHistory = filteredAdherents.filter((adherent) =>
    Boolean(histories?.[adherent.id])
  ).length;

  const withoutHistory = filteredAdherents.length - withHistory;

  const beltDistribution = {};

  filteredAdherents.forEach((adherent) => {
    const name = getCurrentBeltName(adherent.id, histories) || "Sans ceinture";

    beltDistribution[name] = (beltDistribution[name] || 0) + 1;
  });

  const topBelt = Object.entries(beltDistribution).sort((a, b) => b[1] - a[1])[0];

  return {
    totalAll: allAdherents.length,
    totalFiltered: filteredAdherents.length,
    withHistory,
    withoutHistory,
    topBeltName: topBelt?.[0] || "—",
    topBeltCount: topBelt?.[1] || 0,
  };
}